import { Autocomplete, TextField } from "@mui/material";
import styles from "./InputAddress.module.scss";
import { useState, useEffect, useRef, useCallback } from "react";
import { fetcher } from "../../utils/fetcher";
import { useTranslation } from "../../i18n";

interface AddressSuggestion {
  id?: string;
  address_text: string;
}

interface InputAddressProps {
  editMode: boolean;
  address: string | AddressSuggestion | null;
  onAddressChange: (address: string | null | undefined) => void;
  label?: string;
  required?: boolean;
}

export const InputAddress = ({
  editMode,
  address,
  onAddressChange,
  label,
  required = false,
}: InputAddressProps) => {
  const { t } = useTranslation();
  const fieldLabel = label ?? t("common.address");
  const [field, setField] = useState("");
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isUserTypingRef = useRef(false);

  // Synchronizace address prop s field state
  useEffect(() => {
    // Ignore external changes while the user is typing
    if (isUserTypingRef.current) {
      return;
    }

    if (address) {
      const addressText = typeof address === 'string' 
        ? address 
        : address.address_text || "";
      
      setField(addressText);
    } else {
      setField("");
    }
  }, [address]);

  // Debounced fetch pro suggestions
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!editMode || !field.trim()) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await fetcher<AddressSuggestion[]>({
          url: `/address/suggest?query=${encodeURIComponent(field)}`,
        });
        setOptions(data || []);
      } catch (error) {
        setOptions([]);
      }
      setLoading(false);
      isUserTypingRef.current = false;
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [editMode, field]);

  const handleAddressChange = useCallback(
    (_: any, value: AddressSuggestion | null) => {
      isUserTypingRef.current = false;
      onAddressChange(value?.address_text);
    },
    [onAddressChange],
  );

  const handleInputChange = useCallback((_: any, value: string) => {
    isUserTypingRef.current = true;
    setField(value);
  }, []);

  return (
    <div className={styles.inputAddress}>
      <Autocomplete
        disablePortal
        fullWidth
        disableClearable
        options={options}
        loading={loading}
        loadingText={t("common.loading")}
        getOptionLabel={(option) => 
          typeof option === 'string' ? option : option.address_text
        }
        getOptionKey={(option: AddressSuggestion) =>
          `${option.id || option.address_text}-${option.address_text}`
        }
        value={(address as AddressSuggestion | null) || undefined}
        onChange={handleAddressChange}
        inputValue={field}
        onInputChange={handleInputChange}
        disabled={!editMode}
        clearOnBlur={false}
        selectOnFocus={false}
        filterOptions={(x) => x}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              type="text"
              label={fieldLabel}
              variant="outlined"
              size="small"
              fullWidth
              disabled={!editMode}
              slotProps={{ inputLabel: { shrink: true } }}
              required={required}
            />
          );
        }}
      />
    </div>
  );
};

